import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTRIES = [
  ['NL', 'Nederland'], ['BE', 'België'], ['DE', 'Duitsland'], ['CH', 'Zwitserland'], ['IT', 'Italië'],
  ['AT', 'Oostenrijk'], ['FR', 'Frankrijk'], ['LI', 'Liechtenstein'],
];
const FIELDS = ['company_name', 'address', 'postal_code', 'city', 'country', 'phone', 'invoice_email'];
const inputCls = 'mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-zinc-600">{label}</span>
    {children}
  </label>
);

export const DealerDetailsDialog = ({ dealer, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!dealer) return;
    const f = Object.fromEntries(FIELDS.map((k) => [k, dealer[k] || '']));
    const known = COUNTRIES.find(([c, n]) => c === (f.country || '').toUpperCase() || n.toLowerCase() === (f.country || '').toLowerCase());
    f.country = known ? known[0] : (f.country || (dealer.is_foreign_dealer ? 'CH' : 'NL'));
    setForm(f);
  }, [dealer]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/dealers/${dealer.id}/details`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success(`Gegevens bijgewerkt voor ${form.company_name}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  };

  const countryOptions = COUNTRIES.some(([c]) => c === form.country) ? COUNTRIES : [...COUNTRIES, [form.country, form.country]];
  return (
    <Dialog open={!!dealer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dealer-details-dialog">
        <DialogHeader>
          <DialogTitle>Gegevens aanpassen</DialogTitle>
          <DialogDescription>{dealer?.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Bedrijfsnaam">
            <input value={form.company_name || ''} onChange={set('company_name')} className={inputCls} data-testid="dealer-company-input" />
          </Field>
          <Field label="Straat + huisnummer">
            <input value={form.address || ''} onChange={set('address')} className={inputCls} data-testid="dealer-address-input" />
          </Field>
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <Field label="Postcode">
              <input value={form.postal_code || ''} onChange={set('postal_code')} className={inputCls} data-testid="dealer-postal-input" />
            </Field>
            <Field label="Plaats">
              <input value={form.city || ''} onChange={set('city')} className={inputCls} data-testid="dealer-city-input" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Land">
              <select value={form.country || 'NL'} onChange={set('country')} className={inputCls} data-testid="dealer-country-select">
                {countryOptions.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
              </select>
            </Field>
            <Field label="Telefoon">
              <input value={form.phone || ''} onChange={set('phone')} className={inputCls} data-testid="dealer-phone-input" />
            </Field>
          </div>
          <Field label="E-mail voor facturen (leeg = login e-mail)">
            <input type="email" value={form.invoice_email || ''} onChange={set('invoice_email')} placeholder={dealer?.email}
                   className={inputCls} data-testid="dealer-invoice-email-input" />
          </Field>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-zinc-300" data-testid="dealer-details-cancel">Annuleren</button>
          <button onClick={save} disabled={busy || !form.company_name}
                  className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-40" data-testid="dealer-details-save">
            {busy ? 'Opslaan…' : 'Opslaan'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
